import { createSlice } from '@reduxjs/toolkit'
import {
  getProjectVendorsDataAsync as getRfpProjectVendorsDataAsync,
  getAdditionalDetailsVariablesAsync as getRfpAdditionalDetailsVariablesAsync,
} from 'src/redux/async/rfxManagement/rfpManagementAsync'
import {
  getProjectVendorsDataAsync as getRfiProjectVendorsDataAsync,
  getAdditionalDetailsVariablesAsync as getRfiAdditionalDetailsVariablesAsync,
} from 'src/redux/async/rfxManagement/rfxManagementAsync'

/** Same mapping as project-details when syncing vendor table → Redux for baseline upload & dependents. */
const mapVendorResultToProjectVendorInfo = (result) => {
  if (!Array.isArray(result)) return null
  return result.map((vendor) => ({
    vendor_id: vendor.vendor_id,
    project_id: vendor.project_round_id,
    status: vendor.status,
    error_desc: vendor.error_desc,
    is_bid_error: vendor.is_bid_error,
    ap_supplier_id: vendor?.ap_supplier_id,
    supplier_name: vendor?.supplier_name,
  }))
}

const initialState = {
  projectRoundID: null,
  projectID: null,
  projectName: '',
  projectVendorInfo: '',
  allProjectDetails: {},
  encodedProjectId: null,
  isProjectOwner: false,
  additionalDetailsVariables: [],
}

let user = null
let email = ''
let preferred_username = ''
try {
  user = localStorage.getItem('uname')
  const userObject = JSON.parse(user)

  email = userObject?.email || ''
  preferred_username = userObject?.preferred_username || ''
} catch (err) {
  console.error('Error parsing user data from localStorage:', err)
}

export const projectInfoSlice = createSlice({
  name: 'project-configuration-rfx',
  initialState,
  reducers: {
    setProjectRoundID: (state, action) => {
      state.projectRoundID = action.payload
    },
    setProjectID: (state, action) => {
      state.projectID = action.payload
    },
    setProjectName: (state, action) => {
      state.projectName = action.payload
    },
    setProjectVendorInfo: (state, action) => {
      state.projectVendorInfo = action.payload
    },
    setAllProjectDetails: (state, action) => {
      const { projectDetails, permissions = [] } = action.payload

      state.allProjectDetails = projectDetails

      try {
        const userData = JSON.parse(localStorage.getItem('uname') || '{}')

        const email = userData?.email?.trim()?.toLowerCase() || ''
        const preferredUsername = userData?.preferred_username?.trim()?.toLowerCase() || ''

        const isAdmin =
          userData?.user_roles?.some((role) => role?.role_name?.toLowerCase() === 'admin') ||
          permissions.includes('RFx_Admin')

        const list = [
          ...(projectDetails?.cc_user_email || '')
            .split(',')
            .map((item) => item?.trim()?.toLowerCase())
            .filter(Boolean),
          projectDetails?.email_id?.trim()?.toLowerCase(),
        ].filter(Boolean)

        state.isProjectOwner = isAdmin || list.includes(email) || list.includes(preferredUsername)
      } catch (err) {
        console.error('Error parsing user data:', err)
        state.isProjectOwner = false
      }
    },
    setRoundIdForAllProjectDetails: (state, action) => {
      state.allProjectDetails = { ...state.allProjectDetails, round_id: action.payload }
    },
    setRoundNumberForAllProjectDetails: (state, action) => {
      state.allProjectDetails = { ...state.allProjectDetails, round_number: action.payload }
    },
    setEncodedProjectId: (state, action) => {
      state.encodedProjectId = action.payload
    },
    setAdditionalDetailsVariables: (state, action) => {
      state.additionalDetailsVariables = action.payload
    },
    setIsProjectOwner: (state, action) => {
      const { isOwner, permissions = [] } = action.payload

      try {
        const userData = JSON.parse(localStorage.getItem('uname') || '{}')

        const isAdmin =
          userData?.user_roles?.some((role) => role?.role_name?.toLowerCase() === 'admin') ||
          permissions.includes('RFx_Admin')

        state.isProjectOwner = isAdmin || isOwner
      } catch (err) {
        console.error('Error parsing user data:', err)
        state.isProjectOwner = isOwner
      }
    },
  },
  extraReducers: (builder) => {
    const onGetProjectVendorsFulfilled = (state, action) => {
      const raw = action.payload?.result ?? action.payload?.data?.result
      const mapped = mapVendorResultToProjectVendorInfo(raw)
      if (mapped != null) {
        state.projectVendorInfo = mapped
      }
    }
    const onGetAdditionalDetailsVariablesFulfilled = (state, action) => {
      state.additionalDetailsVariables = action.payload?.data ?? []
    }
    builder
      .addCase(getRfpProjectVendorsDataAsync.fulfilled, onGetProjectVendorsFulfilled)
      .addCase(getRfiProjectVendorsDataAsync.fulfilled, onGetProjectVendorsFulfilled)
      .addCase(
        getRfpAdditionalDetailsVariablesAsync.fulfilled,
        onGetAdditionalDetailsVariablesFulfilled,
      )
      .addCase(
        getRfiAdditionalDetailsVariablesAsync.fulfilled,
        onGetAdditionalDetailsVariablesFulfilled,
      )
      .addCase(getRfpAdditionalDetailsVariablesAsync.rejected, (state) => {
        state.additionalDetailsVariables = []
      })
      .addCase(getRfiAdditionalDetailsVariablesAsync.rejected, (state) => {
        state.additionalDetailsVariables = []
      })
  },
})

export const {
  setAllProjectDetails,
  setProjectRoundID,
  setProjectID,
  setProjectName,
  setProjectVendorInfo,
  setRoundIdForAllProjectDetails,
  setRoundNumberForAllProjectDetails,
  setEncodedProjectId,
  setIsProjectOwner,
  setAdditionalDetailsVariables,
} = projectInfoSlice.actions

export default projectInfoSlice.reducer
